import { useState, useRef, type FormEvent } from 'react';

export default function ContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快与您联系。');
      form.reset();
      return;
    }

    setFormStatus('idle');
    setFormMessage('');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9qrc8nnahb7382ove5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快与您联系。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        if (serverMsg.toLowerCase().includes('spam') || responseText.toLowerCase().includes('spam')) {
          setFormStatus('success');
          setFormMessage('感谢您的留言，我们会尽快与您联系。');
          form.reset();
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后重试或直接联系我们。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试或通过电话/邮箱联系我们。');
    }
  };

  return (
    <form
      ref={formRef}
      data-readdy-form
      id="contact-form"
      onSubmit={handleSubmit}
      className="bg-background-50 rounded-lg border border-background-200/60 p-6 md:p-8 space-y-5"
    >
      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1">发送留言</h3>
      <p className="text-sm text-foreground-500">请填写以下信息，我们的客户关系团队将尽快与您取得联系。</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
            姓名 <span className="text-accent-500">*</span>
          </label>
          <input
            type="text"
            id="contact-name"
            name="name"
            required
            className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
            placeholder="请输入您的姓名"
          />
        </div>

        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
            邮箱 <span className="text-accent-500">*</span>
          </label>
          <input
            type="email"
            id="contact-email"
            name="email"
            required
            className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
            placeholder="请输入您的邮箱"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
            电话
          </label>
          <input
            type="tel"
            id="contact-phone"
            name="phone"
            className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
            placeholder="请输入您的电话"
          />
        </div>

        <div>
          <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-700 mb-1.5">
            公司/机构
          </label>
          <input
            type="text"
            id="contact-company"
            name="company"
            className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
            placeholder="请输入您的公司或机构名称"
          />
        </div>
      </div>

      <div>
        <label htmlFor="contact-interest" className="block text-sm font-medium text-foreground-700 mb-1.5">
          关注领域
        </label>
        <select
          id="contact-interest"
          name="interest"
          className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors cursor-pointer"
        >
          <option value="">请选择您关注的领域</option>
          <option value="resources">豪门资源对接</option>
          <option value="business">高端商务合作</option>
          <option value="social">精英社交圈层</option>
          <option value="investment">国际投资顾问</option>
          <option value="education">精英教育规划</option>
          <option value="lifestyle">品质生活服务</option>
          <option value="other">其他</option>
        </select>
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言内容 <span className="text-accent-500">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={4}
          maxLength={500}
          className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
          placeholder="请输入您的留言内容（不超过500字）"
        />
        <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
      </div>

      <div className="honeypot-field">
        <label htmlFor="company-alt">company</label>
        <input
          type="text"
          id="company-alt"
          name="company_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      {formStatus === 'success' && (
        <div className="p-4 rounded-md bg-green-50 border border-green-200">
          <p className="text-sm text-green-700 flex items-center">
            <i className="ri-checkbox-circle-line mr-2" />
            {formMessage}
          </p>
        </div>
      )}

      {formStatus === 'error' && (
        <div className="p-4 rounded-md bg-red-50 border border-red-200">
          <p className="text-sm text-red-700 flex items-center">
            <i className="ri-error-warning-line mr-2" />
            {formMessage}
          </p>
        </div>
      )}

      <button
        type="submit"
        className="w-full px-6 py-3 text-sm font-medium whitespace-nowrap rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-300 cursor-pointer"
      >
        提交留言
      </button>
    </form>
  );
}